import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MovaraMark } from "@/components/Brand";
import { RiskBadge, PrimaryBadge } from "@/components/StatusBadge";
import { readyToClose, closeoutBlocked, dueLabel, timeAgo, primaryStatus, RISK_STYLES } from "@/lib/format";
import { DATASETS } from "@/sample/sampleDatasets";
import {
  ArrowRight, ArrowLeft, LayoutDashboard, Columns3, TriangleAlert, FileText,
  MessageSquare, ListChecks, X, MapPin, Package, CheckCircle2, AlertTriangle, ChevronRight, ShieldAlert,
  Clock, StickyNote, History, Send, Lightbulb,
} from "lucide-react";

const DOC_STYLE = {
  Received: { c: "#059669", b: "#ECFDF5" }, Uploaded: { c: "#059669", b: "#ECFDF5" },
  Pending: { c: "#D97706", b: "#FFFBEB" }, Missing: { c: "#DC2626", b: "#FEF2F2" },
  Required: { c: "#DC2626", b: "#FEF2F2" }, Assigned: { c: "#D97706", b: "#FFFBEB" },
  "Not Required": { c: "#64748B", b: "#F1F5F9" },
};

const isMissingDocs = (j) => j.documents.some((d) => d.status === "Missing");
const isUpdateDue = (j) => j.issues.includes("Customer update overdue");
const isOverdueAction = (j) => j.next_action_state === "Overdue";
const isNeedsAttention = (j) =>
  j.current_stage !== "Closed" && j.current_stage !== "Ready to Close" &&
  (["Needs Attention", "Delayed"].includes(j.risk_status) || j.issues.length > 0);

const TABS = [
  { key: "today", label: "Today", icon: LayoutDashboard },
  { key: "queue", label: "My Queue", icon: ListChecks },
  { key: "board", label: "Dispatch Board", icon: Columns3 },
  { key: "attention", label: "Hot Shipments", icon: TriangleAlert },
  { key: "documents", label: "Manifests & PODs", icon: FileText },
  { key: "updates", label: "Broker Check-Ins", icon: MessageSquare },
];

function DocChip({ d }) {
  const s = DOC_STYLE[d.status] || DOC_STYLE.Pending;
  return <span className="text-[11px] px-2 py-0.5 rounded-[3px] font-medium" style={{ color: s.c, backgroundColor: s.b }}>{d.type}: {d.status}</span>;
}

function JobCard({ j, onOpen }) {
  const status = primaryStatus(j);
  const RED = ["Missing Documents", "POD Missing", "Closeout Blocked"];
  const AMBER = ["Delayed", "Needs Attention", "Customer Update Due"];
  const tone = RED.includes(status) ? "red" : AMBER.includes(status) ? "amber" : "green";
  const toneWrap = { red: "bg-[#FFF5F5] border-l-4 border-l-[#DC2626]", amber: "bg-[#FFFDF9] border-l-4 border-l-[#D97706]", green: "bg-white border-l-4 border-l-[#059669]" }[tone];
  const toneBadge = { red: "bg-[#DC2626]", amber: "bg-[#D97706]", green: "bg-[#059669]" }[tone];
  const statusLabel = (RISK_STYLES[status] || {}).label || status;
  return (
    <button onClick={() => onOpen(j)} data-testid={`sample-card-${j.job_number}`}
      className={`w-full text-left border border-slate-200 rounded-sm p-3 shadow-[1px_1px_2px_rgba(11,19,43,0.08)] hover:shadow-industrialHover transition-shadow group ${toneWrap}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono-data text-[12px] text-ink font-medium">{j.job_number}</span>
        <span className={`${toneBadge} text-white text-[9px] font-black tracking-widest uppercase px-2 py-0.5 rounded`}>{statusLabel}</span>
      </div>
      <div className="text-[13px] text-ink font-medium mt-1.5 truncate">{j.customer_name}</div>
      <div className="text-[11.5px] text-ink-3 flex items-center gap-1 mt-1 truncate"><MapPin className="h-3 w-3 shrink-0" /> {j.pickup_location} → {j.delivery_location}</div>
      <div className="mt-2 flex items-center justify-between">
        <RiskBadge status={j.risk_status} />
        <span className="text-[10.5px] text-ink-3 font-mono-data">{j.assigned_owner.split(" ")[0]}</span>
      </div>
    </button>
  );
}

function Metric({ label, value, tone, onClick }) {
  return (
    <button onClick={onClick} data-testid={`sample-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="text-left bg-surface border border-line-strong rounded-sm px-4 py-3.5 shadow-industrialCard hover:shadow-industrialHover transition-shadow group">
      <div className="font-mono-data text-[24px] font-semibold leading-none" style={{ color: value > 0 ? tone : "#A6AEB6" }}>{value}</div>
      <div className="op-label mt-2 leading-tight">{label}</div>
    </button>
  );
}

function DetailDrawer({ j, onClose }) {
  if (!j) return null;
  const blockers = j.current_stage === "Ready to Close" ? j.close_blockers : j.rtc_blockers;
  const blocked = closeoutBlocked(j);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="sample-detail-drawer">
      <div className="absolute inset-0 bg-ink/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-app-bg h-full overflow-y-auto mv-scroll shadow-xl border-l border-line">
        <div className="sticky top-0 bg-sidebar text-white px-5 py-3.5 flex items-center justify-between">
          <span className="font-mono-data text-[13px]">{j.job_number}</span>
          <button onClick={onClose} data-testid="sample-detail-close" className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <h3 className="font-heading text-lg font-semibold text-ink">{j.customer_name}</h3>
            <p className="text-[12.5px] text-ink-2 flex items-center gap-1.5 mt-1"><MapPin className="h-3.5 w-3.5 text-steel" /> {j.pickup_location} → {j.delivery_location}</p>
            <div className="flex items-center gap-2 mt-3"><PrimaryBadge job={j} /><RiskBadge status={j.risk_status} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-[13px]">
            <div><div className="op-label mb-0.5">Stage</div>{j.current_stage}</div>
            <div><div className="op-label mb-0.5">Owner</div>{j.assigned_owner}</div>
            <div><div className="op-label mb-0.5">Next action</div>{j.next_action}</div>
            <div><div className="op-label mb-0.5">Due</div>{j.next_action_due_date ? dueLabel(j.next_action_due_date) : "—"}</div>
          </div>

          {blocked && (
            <div className="rounded-sm border border-warn/40 bg-[#E9EEF3] p-3" data-testid="sample-detail-blockers">
              <div className="flex items-center gap-1.5 text-warn text-[13px] font-semibold"><ShieldAlert className="h-4 w-4" /> {j.current_stage === "Ready to Close" ? "Blocked from Closing" : "Blocked from Ready to Close"}</div>
              <ul className="mt-2 space-y-1">
                {blockers.map((b, idx) => <li key={idx} className="text-[12.5px] text-ink-2 flex items-start gap-1.5"><span className="text-warn leading-5">•</span> {b.name || b.message}{b.owner ? ` · ${b.owner}` : ""}</li>)}
              </ul>
            </div>
          )}

          <div>
            <div className="op-label mb-2">Manifests &amp; PODs</div>
            <div className="flex flex-wrap gap-1.5">{j.documents.map((d) => <DocChip key={d.type} d={d} />)}</div>
          </div>

          {(j.current_stage === "Ready to Close" || j.current_stage === "Delivered") && (
            <div>
              <div className="op-label mb-2">Closeout checklist</div>
              <div className="space-y-1.5">
                {Object.entries(j.closeout_checklist).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[12.5px] text-ink-2">
                    {v ? <CheckCircle2 className="h-3.5 w-3.5 text-ok" /> : <span className="h-3.5 w-3.5 rounded-[3px] border border-line inline-block" />}
                    {k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="op-label mb-2">Customer updates</div>
            {j.customer_updates.length === 0 ? <p className="text-[12.5px] text-ink-3">No updates yet.</p> : (
              <div className="space-y-2.5">
                {j.customer_updates.map((u) => (
                  <div key={u.id} className="border-l-2 border-steel/40 pl-3">
                    <p className="text-[12.5px] text-ink">{u.message}</p>
                    <p className="text-[11px] text-ink-3 font-mono-data mt-0.5">{u.sent_by} · {timeAgo(u.sent_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {j.suggested_update && (
            <div className="rounded-sm border border-line bg-surface-alt p-3" data-testid="sample-detail-suggested">
              <div className="op-label flex items-center gap-1.5 mb-1"><Lightbulb className="h-3.5 w-3.5 text-steel" /> Suggested next update</div>
              <p className="text-[12.5px] text-ink-2">{j.suggested_update}</p>
            </div>
          )}

          {j.internal_notes?.length > 0 && (
            <div>
              <div className="op-label mb-2 flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5" /> Internal notes</div>
              <div className="space-y-2">
                {j.internal_notes.map((nt) => (
                  <div key={nt.id} className="bg-surface-alt border border-line rounded-sm px-3 py-2">
                    <p className="text-[12.5px] text-ink-2">{nt.body}</p>
                    <p className="text-[11px] text-ink-3 font-mono-data mt-0.5">{nt.author} · {timeAgo(nt.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {j.timeline?.length > 0 && (
            <div>
              <div className="op-label mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Activity timeline</div>
              <ol className="relative border-l border-line ml-1.5 space-y-3">
                {[...j.timeline].reverse().map((e) => (
                  <li key={e.id} className="pl-4 relative">
                    <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-steel" />
                    <p className="text-[12.5px] text-ink">{e.message}</p>
                    <p className="text-[11px] text-ink-3 font-mono-data mt-0.5">{e.actor} · {timeAgo(e.timestamp)}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SampleBoard({ region = "caribbean" }) {
  const dataset = DATASETS[region] || DATASETS.caribbean;
  const SAMPLE_STAGES = dataset.stages;
  const DEMO_USER = dataset.demoUser;
  const [tab, setTab] = useState("today");
  const [active, setActive] = useState(null);
  const jobs = dataset.jobs;

  const m = useMemo(() => {
    const a = jobs.filter((j) => j.current_stage !== "Closed");
    return {
      active: a.length,
      needs_attention: jobs.filter(isNeedsAttention).length,
      missing_docs: a.filter(isMissingDocs).length,
      updates_due: jobs.filter(isUpdateDue).length,
      ready_to_close: jobs.filter(readyToClose).length,
      closeout_blocked: jobs.filter(closeoutBlocked).length,
      closed: jobs.filter((j) => j.current_stage === "Closed").length,
    };
  }, [jobs]);

  const open = (j) => setActive(j);
  const queueOwner = DEMO_USER;
  const myJobs = jobs.filter((j) => j.assigned_owner === queueOwner && j.current_stage !== "Closed");
  const missingDocNames = (j) => j.documents.filter((d) => d.status === "Missing").map((d) => d.type).join(", ");
  const queueSections = [
    { label: "Overdue Next Actions", list: myJobs.filter(isOverdueAction),
      hint: (j) => `Overdue: ${j.next_action} · due ${dueLabel(j.next_action_due_date)}` },
    { label: "Customer Updates Due", list: myJobs.filter(isUpdateDue),
      hint: (j) => `Update due ${j.next_update_due?.toLowerCase() || "now"} · last ${j.last_update_sent ? timeAgo(j.last_update_sent) : "—"}` },
    { label: "Missing Documents", list: myJobs.filter(isMissingDocs),
      hint: (j) => `Missing: ${missingDocNames(j)}` },
    { label: "Closeout Blocked", list: myJobs.filter(closeoutBlocked),
      hint: (j) => `Blocked: ${(j.close_blockers[0] || j.rtc_blockers[0])?.name || (j.close_blockers[0] || j.rtc_blockers[0])?.message || "closeout requirement"}` },
    { label: "Ready for Final Close", list: myJobs.filter((j) => readyToClose(j) && j.can_close),
      hint: () => "All closeout requirements complete · Mark as Closed" },
    { label: "Assigned Active Jobs", list: myJobs, hint: (j) => `${j.current_stage} · next: ${j.next_action}` },
  ];

  return (
    <div className="bg-app-bg min-h-screen">
      {/* Demo banner */}
      <div className="bg-sidebar text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-3">
          <p className="text-[12.5px] sm:text-[13px] flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-steel" /> <span className="font-medium">Sample Board</span> — {dataset.label} demo workspace with sample shipment data.</p>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/request-access" data-testid="sample-request-btn" className="h-8 px-3 rounded-[5px] bg-white text-brand text-[12.5px] font-medium flex items-center gap-1.5 hover:bg-white/90 transition-colors">Request a Visibility Walkthrough <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </div>
      </div>

      {/* App header + tabs */}
      <header className="sticky top-0 z-20 bg-app-bg/95 backdrop-blur border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-brand shrink-0"><MovaraMark className="h-5 w-6" /><span className="font-heading font-semibold text-[15px] text-ink hidden sm:inline">MovaRaBoard</span></Link>
          <nav className="flex items-center gap-1 overflow-x-auto mv-scroll -mx-1 px-1">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} data-testid={`sample-tab-${key}`} onClick={() => setTab(key)}
                className={`h-9 px-3 rounded-[5px] text-[12.5px] font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${tab === key ? "bg-brand text-white" : "text-ink-2 hover:bg-surface-alt"}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {tab === "today" && (
          <div>
            <h2 className="font-heading text-lg font-semibold text-ink mb-1">Today</h2>
            <p className="text-[12.5px] text-ink-2 mb-4">This sample board uses demo shipment data to show how a logistics team can track active jobs, blockers, documents, updates, and closeout.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5 mb-6">
              <Metric label="Active Jobs" value={m.active} tone="#0B132B" onClick={() => setTab("board")} />
              <Metric label="Hot Shipments" value={m.needs_attention} tone="#D97706" onClick={() => setTab("attention")} />
              <Metric label="Missing Documents" value={m.missing_docs} tone="#DC2626" onClick={() => setTab("documents")} />
              <Metric label="Customer Updates Due" value={m.updates_due} tone="#D97706" onClick={() => setTab("updates")} />
              <Metric label="Ready to Close" value={m.ready_to_close} tone="#059669" onClick={() => setTab("board")} />
              <Metric label="Closeout Blocked" value={m.closeout_blocked} tone="#DC2626" onClick={() => setTab("attention")} />
              <Metric label="Closed" value={m.closed} tone="#64748B" onClick={() => setTab("board")} />
            </div>
            <p className="op-label mb-2">Jobs that need attention now</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {jobs.filter(isNeedsAttention).map((j) => <JobCard key={j.id} j={j} onOpen={open} />)}
            </div>
          </div>
        )}

        {tab === "board" && (
          <div className="overflow-x-auto mv-scroll pb-2">
            <div className="flex gap-3 min-w-max">
              {SAMPLE_STAGES.map((stage) => {
                const list = jobs.filter((j) => j.current_stage === stage);
                return (
                  <div key={stage} className="w-64 shrink-0">
                    <div className="flex items-center justify-between mb-2 px-1">
                      <span className="op-label">{stage}</span>
                      <span className="font-bold bg-slate-200 text-slate-800 text-xs px-2 py-0.5 rounded-full">{list.length}</span>
                    </div>
                    <div className="space-y-2.5">{list.map((j) => <JobCard key={j.id} j={j} onOpen={open} />)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "attention" && (
          <div>
            <h2 className="font-heading text-lg font-semibold text-ink mb-4">Hot Shipments</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {jobs.filter((j) => isNeedsAttention(j) || closeoutBlocked(j)).map((j) => <JobCard key={j.id} j={j} onOpen={open} />)}
            </div>
          </div>
        )}

        {tab === "documents" && (
          <div>
            <h2 className="font-heading text-lg font-semibold text-ink mb-4">Manifests &amp; PODs</h2>
            <div className="bg-surface border border-line rounded-md divide-y divide-line">
              {jobs.filter((j) => j.current_stage !== "Closed").map((j) => (
                <button key={j.id} onClick={() => open(j)} data-testid={`sample-doc-row-${j.job_number}`} className="w-full text-left px-4 py-3 hover:bg-surface-alt transition-colors flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="font-mono-data text-[12px] text-ink font-medium w-16">{j.job_number}</span>
                  <span className="text-[13px] text-ink flex-1 min-w-[120px]">{j.customer_name}</span>
                  <span className="flex flex-wrap gap-1.5">{j.documents.map((d) => <DocChip key={d.type} d={d} />)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "updates" && (
          <div>
            <h2 className="font-heading text-lg font-semibold text-ink mb-1">Broker Check-Ins</h2>
            <p className="text-[12.5px] text-ink-2 mb-4">Update health across active shipments — what was last sent, what's due, and a suggested message.</p>
            <div className="bg-surface border border-line rounded-md divide-y divide-line">
              {jobs.filter((j) => j.current_stage !== "Closed").map((j) => {
                const overdue = isUpdateDue(j) || ["Now", "Today"].includes(j.next_update_due);
                return (
                  <button key={j.id} onClick={() => open(j)} data-testid={`sample-update-row-${j.job_number}`} className="w-full text-left px-4 py-3.5 hover:bg-surface-alt transition-colors">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-mono-data text-[12px] text-ink font-medium w-16">{j.job_number}</span>
                      <span className="text-[13px] text-ink font-medium flex-1 min-w-[140px]">{j.customer_name}</span>
                      <span className="text-[11.5px] text-ink-3">{j.current_stage}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-[3px] font-medium ${overdue ? "text-danger bg-[#E9EEF3]" : "text-ink-2 bg-surface-alt border border-line"}`}>{j.update_status}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1.5 text-[11.5px] text-ink-3">
                      <span className="flex items-center gap-1"><History className="h-3 w-3" /> Last: {j.last_update_sent ? timeAgo(j.last_update_sent) : "No update yet"}</span>
                      <span className="flex items-center gap-1">Next due: <span className={overdue ? "font-medium" : ""}>{j.next_update_due}</span></span>
                    </div>
                    {j.suggested_update && (
                      <div className="mt-2 flex items-start gap-1.5 text-[12px] text-ink-2 bg-surface-alt border border-line rounded-sm px-2.5 py-2">
                        <Lightbulb className="h-3.5 w-3.5 text-steel shrink-0 mt-0.5" />
                        <span><span className="op-label mr-1">Suggested:</span>{j.suggested_update}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === "queue" && (
          <div>
            <h2 className="font-heading text-lg font-semibold text-ink">My Queue</h2>
            <p className="text-[13px] text-ink-2 mb-4">Work assigned to {queueOwner}, grouped by what needs action.</p>
            <div className="space-y-4">
              {queueSections.map((sec) => (
                <div key={sec.label} className="bg-surface border border-line rounded-md overflow-hidden">
                  <div className="px-4 py-3 border-b border-line flex items-center gap-2">
                    <h3 className="font-heading text-[14px] font-semibold text-ink">{sec.label}</h3>
                    <span className="font-bold bg-slate-200 text-slate-800 text-xs px-2 py-0.5 rounded-full">{sec.list.length}</span>
                  </div>
                  {sec.list.length === 0 ? <p className="px-4 py-5 text-center text-[12.5px] text-ink-3">Nothing here.</p> : (
                    <div className="divide-y divide-line">
                      {sec.list.map((j) => (
                        <button key={j.id} onClick={() => open(j)} data-testid={`sample-queue-${j.job_number}`} className="w-full text-left px-4 py-2.5 hover:bg-surface-alt transition-colors flex items-center justify-between gap-3 group">
                          <span className="min-w-0">
                            <span className="flex items-center gap-2.5">
                              <span className="font-mono-data text-[12px] text-ink font-medium">{j.job_number}</span>
                              <span className="text-[13px] text-ink truncate">{j.customer_name}</span>
                            </span>
                            <span className="block text-[11.5px] text-ink-3 truncate mt-0.5">{sec.hint(j)}</span>
                          </span>
                          <span className="flex items-center gap-2 shrink-0"><RiskBadge status={j.risk_status} /><ChevronRight className="h-3.5 w-3.5 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" /></span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Static conversion CTA — bottom, does not interrupt the demo */}
        <div data-testid="sample-cta-banner" className="mt-8 rounded-md border border-line bg-surface p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-[15.5px] sm:text-base font-semibold text-ink">Want to see how this would work for your logistics team?</h3>
            <p className="mt-1.5 text-[13px] text-ink-2 leading-relaxed max-w-2xl">
              Request a visibility walkthrough and we'll show how MovaRaBoard can organize your shipment
              jobs, updates, documents, blockers, and closeout.
            </p>
          </div>
          <Link
            to="/request-access"
            data-testid="sample-cta-btn"
            className="h-11 px-5 rounded-[5px] bg-brand text-white text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-brand-dark transition-colors shrink-0 whitespace-nowrap"
          >
            Request a Visibility Walkthrough <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <Link to="/" data-testid="sample-back-home" className="text-[13px] text-ink-2 hover:text-brand flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
          <p className="text-[12px] text-ink-3 font-mono-data">Sample data · MovaRaBoard demo workspace</p>
        </div>
      </footer>

      <DetailDrawer j={active} onClose={() => setActive(null)} />
    </div>
  );
}
